import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  IconCircleQuestionOutline18,
  IconOffice2Outline18,
  IconPaletteOutline18,
  IconStampOutline18,
} from 'nucleo-ui-outline-18'
import { useMemo } from 'react'
import { BrandColorField } from '~/components/settings/brand-color-field'
import { SettingsPage, SettingsRow, SettingsSection } from '~/components/settings/settings-page'
import { SettingsTextField } from '~/components/settings/settings-text-field'
import { WorkspaceLogoField } from '~/components/settings/workspace-logo-field'
import { titleFor } from '~/lib/route-title'
import { validateWith } from '~/lib/validation'
import { makeWorkspaceNameFieldSchema } from '~/lib/workspace'
import { useWorkspace } from '~/lib/workspace-context'
import { api } from '~convex/_generated/api'
import { REGISTRY_FIELD_MAX, type RegistryField } from '~convex/lib/workspace'

/**
 * The studio registry, in the order it prints on a letterhead: who the studio
 * is, where it is, what it is registered as, and how to reach it.
 *
 * Every field is optional and saves on its own, so an office can fill in the
 * CNPJ today and the CAU number whenever it has one. A blank field is not a
 * blank line on the document — the letterhead drops it entirely.
 */
const REGISTRY_FIELDS: {
  key: RegistryField
  label: MessageDescriptor
  description?: MessageDescriptor
}[] = [
  {
    description: msg`Appears under your name on the letterhead, for example, "arquitetos associados".`,
    key: 'tagline',
    label: msg`Tagline`,
  },
  {
    description: msg`Razão social, for contracts. Your display name stays the wordmark.`,
    key: 'legalName',
    label: msg`Legal name`,
  },
  { key: 'addressLine', label: msg`Address` },
  { key: 'city', label: msg`City` },
  { key: 'state', label: msg`State` },
  { description: msg`CNPJ or CPF.`, key: 'taxId', label: msg`Tax ID` },
  {
    description: msg`Your CAU or CREA registration.`,
    key: 'professionalId',
    label: msg`Registration`,
  },
  { key: 'email', label: msg`Contact email` },
  { key: 'phone', label: msg`Contact phone` },
]

export const Route = createFileRoute('/_authenticated/$workspaceSlug/settings/general')({
  component: GeneralSettings,
  head: () => ({ meta: [{ title: titleFor(msg`General settings`) }] }),
})

function GeneralSettings() {
  const { t, i18n } = useLingui()
  const workspace = useWorkspace()
  const updateWorkspace = useConvexMutation(api.workspaces.updateWorkspace)
  const updateRegistry = useConvexMutation(api.workspaces.updateWorkspaceRegistry)
  const { data: logoUploadsEnabled } = useQuery(convexQuery(api.workspaces.logoUploadsEnabled, {}))
  const nameSchema = useMemo(() => makeWorkspaceNameFieldSchema(i18n), [i18n])
  // Mirror the server gate so members get a clear read-only state.
  const isAdmin = workspace.role === 'admin'

  const adminNote = isAdmin ? undefined : <Trans>Only workspace admins can change these.</Trans>

  return (
    <SettingsPage>
      <SettingsSection description={adminNote} icon={IconOffice2Outline18} title={t`Identity`}>
        <SettingsRow htmlFor="workspaceName" label={t`Name`}>
          <SettingsTextField
            autoComplete="off"
            disabled={!isAdmin}
            id="workspaceName"
            // Remount when the workspace in focus changes so the draft tracks it.
            key={workspace._id}
            onSave={(name) => updateWorkspace({ name, workspaceId: workspace._id })}
            validate={(value) => validateWith(nameSchema, value)}
            value={workspace.name}
          />
        </SettingsRow>
      </SettingsSection>

      <SettingsSection icon={IconPaletteOutline18} title={t`Branding`}>
        <SettingsRow label={t`Logo`}>
          <WorkspaceLogoField
            // Admin-only controls, mirroring `generateLogoUploadUrl`'s gate;
            // members still see the current logo.
            canEdit={isAdmin}
            logo={workspace.logo ?? null}
            uploadsEnabled={logoUploadsEnabled ?? false}
            workspaceId={workspace._id}
            workspaceName={workspace.name}
          />
        </SettingsRow>

        <SettingsRow htmlFor="brandColor" label={t`Brand color`}>
          <BrandColorField
            brandColor={workspace.brandColor ?? null}
            disabled={!isAdmin}
            // Remount when the workspace in focus changes so the draft tracks it.
            key={workspace._id}
            logoPalette={workspace.logoPalette ?? []}
            workspaceId={workspace._id}
          />
        </SettingsRow>
      </SettingsSection>

      <SettingsSection
        description={<Trans>Optional details for contracts and letterheads.</Trans>}
        icon={IconStampOutline18}
        title={t`Registry`}
      >
        {REGISTRY_FIELDS.map((field) => (
          <SettingsRow
            description={field.description === undefined ? undefined : i18n._(field.description)}
            htmlFor={field.key}
            key={field.key}
            label={i18n._(field.label)}
          >
            <SettingsTextField
              autoComplete="off"
              disabled={!isAdmin}
              id={field.key}
              // Remount when the workspace in focus changes so the draft tracks it.
              key={workspace._id}
              onSave={(value) =>
                updateRegistry({ field: field.key, value, workspaceId: workspace._id })
              }
              validate={(value) =>
                value.trim().length > REGISTRY_FIELD_MAX[field.key]
                  ? t`Keep this under ${REGISTRY_FIELD_MAX[field.key]} characters.`
                  : null
              }
              value={workspace[field.key] ?? ''}
            />
          </SettingsRow>
        ))}
      </SettingsSection>

      <SettingsSection bare icon={IconCircleQuestionOutline18} title={t`Another workspace`}>
        <div className="rounded-lg border border-border/60 px-4 py-3.5">
          <p className="text-pretty text-muted-foreground text-sm">
            <Trans>
              Use the support bubble in the bottom-right corner to create another workspace.
            </Trans>
          </p>
        </div>
      </SettingsSection>
    </SettingsPage>
  )
}
